import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BdsAccordion,
  BdsAccordionHeader,
  BdsAccordionBody,
  BdsButton,
  BdsIcon,
  BdsInput,
  BdsRadio,
  BdsRadioGroup,
} from '@/core/constants/webComponents';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { CreateFileConfirmationModal } from './CreateFileConfirmationModal';
import './CreateFileCard.scss';

interface CreateFileCardProps {
  onClose: () => void;
  onConfirm: (data: CreateFileData) => void;
  totalFilteredPosts: number;
}

export interface CreateFileData {
  fileName: string;
  postsCount: number;
}

type ConfidenceLevel = '90' | '95' | '99';

const zMap: Record<ConfidenceLevel, number> = { '90': 1.645, '95': 1.96, '99': 2.576 };

function calculateSample(confidence: ConfidenceLevel, margin: number, population: number): number {
  if (margin <= 0 || population <= 0) return 0;
  const z = zMap[confidence];
  const p = 0.5;
  const e = margin / 100;
  const n0 = (z * z * p * (1 - p)) / (e * e);
  return Math.ceil(n0 / (1 + (n0 - 1) / population));
}

export const CreateFileCard: React.FC<CreateFileCardProps> = ({ onClose, onConfirm, totalFilteredPosts }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const [fileName, setFileName] = useState('');
  const [postsCount, setPostsCount] = useState(0);
  const [confidenceLevel, setConfidenceLevel] = useState<ConfidenceLevel>('95');
  const [errorMargin, setErrorMargin] = useState(0);
  const [totalPopulation, setTotalPopulation] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmPostsCount, setConfirmPostsCount] = useState(0);
  const [fileNameError, setFileNameError] = useState(false);
  const [postsCountError, setPostsCountError] = useState(false);

  const necessarySample = useMemo(
    () => calculateSample(confidenceLevel, errorMargin, totalPopulation),
    [confidenceLevel, errorMargin, totalPopulation],
  );

  useEffect(() => {
    const fileNameEl = document.getElementById('create-file-name');
    const postsCountEl = document.getElementById('create-file-posts-count');
    const errorMarginEl = document.getElementById('create-file-error-margin');
    const totalPopulationEl = document.getElementById('create-file-total-population');
    const radioGroupEl = document.getElementById('create-file-confidence');

    const handleFileName = (e: any) => {
      const val = e.detail?.value ?? e.target?.value ?? '';
      setFileName(val);
      if (val.trim()) setFileNameError(false);
    };
    const handlePostsCount = (e: any) => {
      const raw = Number(e.detail?.value ?? e.target?.value ?? 0);
      const val = Math.min(raw, 300000);
      setPostsCount(val);
      if (val !== raw) {
        const el = document.getElementById('create-file-posts-count') as any;
        if (el) el.value = val;
      }
      if (val > 0) setPostsCountError(false);
    };
    const handleErrorMargin = (e: any) => setErrorMargin(Number(e.detail?.value ?? e.target?.value ?? 0));
    const handleTotalPopulation = (e: any) => setTotalPopulation(Number(e.detail?.value ?? e.target?.value ?? 0));
    const handleRadioChange = (e: any) => setConfidenceLevel((e.detail?.value ?? '95') as ConfidenceLevel);

    fileNameEl?.addEventListener('bdsInput', handleFileName);
    fileNameEl?.addEventListener('bdsChange', handleFileName);
    postsCountEl?.addEventListener('bdsInput', handlePostsCount);
    postsCountEl?.addEventListener('bdsChange', handlePostsCount);
    errorMarginEl?.addEventListener('bdsInput', handleErrorMargin);
    errorMarginEl?.addEventListener('bdsChange', handleErrorMargin);
    totalPopulationEl?.addEventListener('bdsInput', handleTotalPopulation);
    totalPopulationEl?.addEventListener('bdsChange', handleTotalPopulation);
    radioGroupEl?.addEventListener('bdsRadioGroupChange', handleRadioChange);

    return () => {
      fileNameEl?.removeEventListener('bdsInput', handleFileName);
      fileNameEl?.removeEventListener('bdsChange', handleFileName);
      postsCountEl?.removeEventListener('bdsInput', handlePostsCount);
      postsCountEl?.removeEventListener('bdsChange', handlePostsCount);
      errorMarginEl?.removeEventListener('bdsInput', handleErrorMargin);
      errorMarginEl?.removeEventListener('bdsChange', handleErrorMargin);
      totalPopulationEl?.removeEventListener('bdsInput', handleTotalPopulation);
      totalPopulationEl?.removeEventListener('bdsChange', handleTotalPopulation);
      radioGroupEl?.removeEventListener('bdsRadioGroupChange', handleRadioChange);
    };
  }, []);

  const handleUseValue = () => {
    setPostsCount(necessarySample);
    const el = document.getElementById('create-file-posts-count') as any;
    if (el) el.value = necessarySample;
  };

  const handleGenerateSample = () => {
    let hasError = false;
    if (!fileName.trim()) {
      setFileNameError(true);
      hasError = true;
    }
    if (postsCount <= 0) {
      setPostsCountError(true);
      hasError = true;
    }
    if (hasError) return;
    const finalPostsCount = Math.min(postsCount, 300000);
    setConfirmPostsCount(finalPostsCount);
    setShowConfirmation(true);
  };

  const handleConfirmClick = () => {
    if (!fileName.trim()) {
      setFileNameError(true);
      return;
    }
    const finalPostsCount = Math.min(totalFilteredPosts, 300000);
    if (finalPostsCount <= 0) {
      showToast({
        variant: 'warning',
        title: t('cardPosts.tagModalConfirmTitle'),
        text: t('file.invalidPostsCount'),
        icon: 'warning',
      }).catch(() => {});
      return;
    }
    setConfirmPostsCount(finalPostsCount);
    setShowConfirmation(true);
  };

  const handleFinalConfirm = () => {
    onConfirm({ fileName, postsCount: confirmPostsCount });
    setShowConfirmation(false);
  };

  return (
    <div className="create-file-card">
      {/* Header */}
      <div className="create-file-card__header">
        <span className="create-file-card__title">{t('file.title')}</span>
        <button className="create-file-card__close" onClick={onClose} aria-label={t('file.close')}>
          <BdsIcon name="close" />
        </button>
      </div>

      {/* File name input */}
      <BdsInput
        id="create-file-name"
        label={t('file.nameLabel')}
        placeholder={t('file.namePlaceholder')}
        danger={fileNameError}
        errorMessage={fileNameError ? t('file.noNameWarning') : ''}
      />

      {/* Posts count row */}
      <div className="create-file-card__row">
        <BdsInput
          id="create-file-posts-count"
          type="number"
          label={t('file.postsCount')}
          value={String(postsCount)}
          class="create-file-card__input--flex"
          danger={postsCountError}
          errorMessage={postsCountError ? t('file.invalidPostsCount') : ''}
        />
        <BdsButton
          variant="primary"
          class="create-file-card__generate-btn"
          onClick={handleGenerateSample}
        >
          {t('file.generateSample')}
        </BdsButton>
      </div>

      {/* Accordion: sample confidence calculator */}
      <BdsAccordion class="create-file-card__accordion">
        <BdsAccordionHeader accordion-title={t('file.calculator')} />
        <BdsAccordionBody>
          <div className="create-file-card__calculator">
            {/* Confidence level radio buttons */}
            <div className="create-file-card__confidence-row">
              <span className="create-file-card__confidence-label">{t('file.confidenceLevel')}</span>
              <BdsRadioGroup id="create-file-confidence" class="create-file-card__radio-group">
                <BdsRadio value="90" label="90%" />
                <BdsRadio value="95" label="95%" checked />
                <BdsRadio value="99" label="99%" />
              </BdsRadioGroup>
            </div>

            {/* Error margin + Total population */}
            <div className="create-file-card__row">
              <BdsInput
                id="create-file-error-margin"
                type="number"
                label={t('file.errorMargin')}
                value={String(errorMargin)}
                class="create-file-card__input--flex"
              />
              <BdsInput
                id="create-file-total-population"
                type="number"
                label={t('file.totalPopulation')}
                value={String(totalPopulation)}
                class="create-file-card__input--flex"
              />
            </div>

            {/* Necessary sample + Use this value */}
            <div className="create-file-card__row">
              <BdsInput
                id="create-file-necessary-sample"
                label={t('file.necessarySample')}
                value={String(necessarySample)}
                readonly
                class="create-file-card__input--flex"
              />
              <BdsButton
                variant="solid"
                color="content"
                class="create-file-card__use-btn"
                onClick={handleUseValue}
              >
                {t('file.useValue')}
              </BdsButton>
            </div>
          </div>
        </BdsAccordionBody>
      </BdsAccordion>

      {/* Footer buttons */}
      <div className="create-file-card__footer">
        <BdsButton variant="outline" color="content" onClick={onClose}>
          {t('file.cancel')}
        </BdsButton>
        <BdsButton
          variant="primary"
          class="create-file-card__confirm-btn"
          onClick={handleConfirmClick}
        >
          {t('file.confirm')}
        </BdsButton>
      </div>

      {showConfirmation && (
        <CreateFileConfirmationModal
          fileName={fileName}
          postsCount={confirmPostsCount}
          onCancel={() => setShowConfirmation(false)}
          onConfirm={handleFinalConfirm}
        />
      )}
    </div>
  );
};
